import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { fetchSavedListings } from '../../ducks/savedListings.duck';
import { types as sdkTypes } from '../../util/sdkLoader';

const savedKeyFromItem = (listingId, timeslotId) => {
  const lid = typeof listingId === 'string' ? listingId : listingId?.uuid;
  const tid = timeslotId ? (typeof timeslotId === 'string' ? timeslotId : timeslotId?.uuid) : null;
  return tid ? `${lid}::${tid}` : `${lid}::`;
};

// ================ Action types ================ //

export const FETCH_SAVED_EVENTS_REQUEST = 'app/SavedEventsPage/FETCH_SAVED_EVENTS_REQUEST';
export const FETCH_SAVED_EVENTS_SUCCESS = 'app/SavedEventsPage/FETCH_SAVED_EVENTS_SUCCESS';
export const FETCH_SAVED_EVENTS_ERROR = 'app/SavedEventsPage/FETCH_SAVED_EVENTS_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  displayItems: [],
};

export default function savedEventsPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_SAVED_EVENTS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_SAVED_EVENTS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: null,
        displayItems: payload || [],
      };
    case FETCH_SAVED_EVENTS_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const getDisplayItems = state => state.SavedEventsPage?.displayItems || [];
export const fetchSavedEventsInProgress = state => state.SavedEventsPage?.fetchInProgress || false;
export const fetchSavedEventsError = state => state.SavedEventsPage?.fetchError;

// ================ Thunks ================ //

/**
 * Fetch saved events - listings with timeslot dates from stored data.
 * 1. Fetch saved IDs (with timeslotStart/timeslotEnd) from our API
 * 2. Fetch each listing via sdk.listings.show
 * 3. Use stored timeslot_start/end for display - no search needed.
 */
export const fetchSavedEvents = config => (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_SAVED_EVENTS_REQUEST });

  return dispatch(fetchSavedListings())
    .then(() => {
      const state = getState();
      const savedItems = state.savedListings?.savedItems || [];

      if (savedItems.length === 0) {
        dispatch({ type: FETCH_SAVED_EVENTS_SUCCESS, payload: [] });
        return [];
      }

      const listingFields = config?.listing?.listingFields;
      const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
        config?.layout?.listingImage || {};
      const aspectRatio = aspectHeight / aspectWidth;

      const dedupedItems = Array.from(
        new Map(
          savedItems.map(s => {
            const key = savedKeyFromItem(s.listingId, s.timeslotId);
            return [key, s];
          })
        ).values()
      );

      return Promise.all(
        dedupedItems.map(({ listingId, timeslotId, timeslotStart, timeslotEnd }) => {
          const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
          return sdk.listings
            .show({
              id: new sdkTypes.UUID(id),
              include: ['author', 'author.profileImage', 'images'],
              'fields.listing': [
                'title',
                'geolocation',
                'price',
                'deleted',
                'state',
                'availabilityPlan',
                'publicData.listingType',
                'publicData.categoryLevel1',
                'publicData.categoryLevel2',
                'publicData.mode',
                'publicData.duration',
                'publicData.transactionProcessAlias',
                'publicData.unitType',
                'publicData.averageReviewRating',
                'publicData.location',
                'publicData.unlisted',
                'publicData.visibility',
                'publicData.digitalProductContentType',
                'publicData.digitalProductFormat',
              ],
              'fields.image': [
                `variants.${variantPrefix}`,
                `variants.${variantPrefix}-2x`,
                `variants.${variantPrefix}-4x`,
              ],
              ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
              ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
              ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
            })
            .then(response => {
              const listing = denormalisedResponseEntities(response)?.[0];
              if (listing) {
                dispatch(addMarketplaceEntities(response, { listingFields }));
              }
              const timeslot =
                timeslotStart && timeslotEnd
                  ? { attributes: { start: timeslotStart, end: timeslotEnd } }
                  : null;
              return {
                listing,
                timeslotId,
                timeslot,
              };
            })
            .catch(() => ({ listing: null, timeslotId, timeslot: null }));
        })
      ).then(results => {
        const displayItems = results
          .filter(r => r.listing)
          .map(({ listing, timeslotId, timeslot }) => ({
            listingId: listing?.id?.uuid,
            timeslotId,
            listing,
            timeslot,
          }));
        dispatch({ type: FETCH_SAVED_EVENTS_SUCCESS, payload: displayItems });
      });
    })
    .catch(e => {
      dispatch({ type: FETCH_SAVED_EVENTS_ERROR, payload: storableError(e) });
      throw e;
    });
};
